/*@jsxRuntime automatic @jsxImportSource react*/
/*<!-- vale Hightouch.Substitutions = NO -->*/
/*<!-- vale Hightouch.Substitutions = YES -->*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Lightning sync engine", description = "Compute change data capture in your warehouse instead of on Hightouch infrastructure, for faster, more reliable syncs on large models.", lastUpdatedDate = new Date(1791417600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Lightning sync engine",
        description: "Compute change data capture in your warehouse instead of on Hightouch infrastructure, for faster, more reliable syncs on large models.",
        lastUpdatedDate: "Thu Oct 08 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "How the Lightning sync engine works",
          "depth": 0,
          "id": "how-the-lightning-sync-engine-works"
        }, {
          "value": "Engine comparison",
          "depth": 1,
          "id": "engine-comparison"
        }, {
          "value": "When the Lightning sync engine is required",
          "depth": 1,
          "id": "when-the-lightning-sync-engine-is-required"
        }, {
          "value": "Supported sources",
          "depth": 0,
          "id": "supported-sources"
        }, {
          "value": "Enable the Lightning sync engine",
          "depth": 0,
          "id": "enable-the-lightning-sync-engine"
        }, {
          "value": "Warehouse schemas",
          "depth": 1,
          "id": "warehouse-schemas"
        }, {
          "value": "Migrate from Basic sync engine",
          "depth": 0,
          "id": "migrate-from-basic-sync-engine"
        }, {
          "value": "Migration tips",
          "depth": 1,
          "id": "migration-tips"
        }, {
          "value": "Common errors",
          "depth": 0,
          "id": "common-errors"
        }, {
          "value": "Primary key is not unique",
          "depth": 1,
          "id": "primary-key-is-not-unique"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      em: "em",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      h2: "h2",
      img: "img",
      h3: "h3",
      ul: "ul",
      li: "li",
      ol: "ol",
      code: "code",
      h4: "h4",
      pre: "pre",
      span: "span"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "The Lightning sync engine runs change data capture inside your data warehouse, so syncs on large models are faster and more reliable."
        })
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Data teams running large or feature-rich syncs"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsxs(_components.td, {
              children: ["A ", _jsx(_components.a, {
                href: "#supported-sources",
                children: "supported source"
              }), " and write access to your data warehouse."]
            })]
          })]
        })]
      }), "\n", "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "The Lightning sync engine was previously known as warehouse planning. Both\nterms describe the same feature."
        })
      }), "\n", "\n", _jsx(_components.h2, {
        id: "how-the-lightning-sync-engine-works",
        children: "How the Lightning sync engine works"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch identifies the incremental changes in your data model so that syncs send only the necessary updates to your destinations. For every sync, Hightouch computes what rows to send by pulling all rows within your data model and comparing them with the last sync. This process is called ", _jsx(_components.strong, {
          children: "change data capture"
        }), " (CDC) or ", _jsx(_components.strong, {
          children: "diffing."
        }), " You can find more details in the ", _jsx(_components.a, {
          href: "/syncs/cdc",
          children: "change data capture overview"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "By default, the change data capture computation and file storage happens on Hightouch managed infrastructure. This can be slow for large models that take a long time to query."
      }), "\n", _jsxs(_components.p, {
        children: ["With the ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), ", Hightouch computes change data capture ", _jsx(_components.strong, {
          children: "directly in your warehouse"
        }), ". It stores previously synced data into a special schema managed by Hightouch. Customers with extensive datasets requiring high performance syncs typically use the Lightning sync engine."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/basic-sync-engine.png",
          alt: "Basic sync architecture diagram"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "get-started/lightning-sync-engine.png",
          alt: "Lightning sync engine architecture diagram"
        })
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Hightouch recommends using the Lightning sync engine for ", _jsx(_components.a, {
            href: "#supported-sources",
            children: "supported\nsources"
          }), " when syncing more than 100 thousand rows of data."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "engine-comparison",
        children: "Engine comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), " requires granting write access to your data warehouse, which makes its setup more involved than the Basic sync engine. However, it is more performant and reliable than the Basic engine. This makes it the ideal choice to guarantee faster syncs, especially with large data models. It also unlocks features that are not available with the Basic engine, including ", _jsx(_components.a, {
          href: "/customer-studio/journeys",
          children: "Journeys"
        }), ", ", _jsx(_components.a, {
          href: "/customer-studio/sampling",
          children: "Sampling"
        }), ", ", _jsx(_components.a, {
          href: "/syncs/warehouse-sync-logs",
          children: "Warehouse Sync Logs"
        }), ", ", _jsx(_components.a, {
          href: "/customer-studio/audience-snapshots",
          children: "Audience Snapshots"
        }), ", ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster"
        }), ", and ", _jsx(_components.a, {
          href: "https://hightouch.com/platform/identity-resolution",
          children: "Identity Resolution"
        }), "."]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Criteria"
            }), _jsx(_components.th, {
              children: "Basic sync engine"
            }), _jsx(_components.th, {
              children: "Lightning sync engine"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Performance"
            }), _jsx(_components.td, {
              children: "Slower"
            }), _jsx(_components.td, {
              children: "Quicker (up to 100 times faster)"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Ideal for large data models (over 100 thousand rows)"
            }), _jsx(_components.td, {
              children: "No"
            }), _jsx(_components.td, {
              children: "Yes"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Reliability"
            }), _jsx(_components.td, {
              children: "Normal"
            }), _jsx(_components.td, {
              children: "High"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Resilience to sync interruptions"
            }), _jsx(_components.td, {
              children: "Normal"
            }), _jsx(_components.td, {
              children: "High"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Extra features"
            }), _jsx(_components.td, {
              children: "None"
            }), _jsxs(_components.td, {
              children: [_jsx(_components.a, {
                href: "/customer-studio/journeys",
                children: "Journeys"
              }), ", ", _jsx(_components.a, {
                href: "/customer-studio/sampling",
                children: "Sampling"
              }), ", ", _jsx(_components.a, {
                href: "/syncs/warehouse-sync-logs",
                children: "Warehouse Sync Logs"
              }), ", ", _jsx(_components.a, {
                href: "/customer-studio/audience-snapshots",
                children: "Audience Snapshots"
              }), ", ", _jsx(_components.a, {
                href: "/match-booster/overview",
                children: "Match Booster"
              }), ", ", _jsx(_components.a, {
                href: "https://hightouch.com/platform/identity-resolution",
                children: "Identity Resolution"
              }), ", experiment ", _jsx(_components.a, {
                href: "/customer-studio/experiments",
                children: "holdout group logs"
              })]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Ease of setup"
            }), _jsx(_components.td, {
              children: "Simpler"
            }), _jsx(_components.td, {
              children: "More involved"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Location of change data capture"
            }), _jsx(_components.td, {
              children: "Hightouch infrastructure"
            }), _jsx(_components.td, {
              children: "Warehouse schemas managed by Hightouch"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Required warehouse permissions"
            }), _jsx(_components.td, {
              children: "Read-only"
            }), _jsx(_components.td, {
              children: "Read and write"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Ability to switch"
            }), _jsx(_components.td, {
              children: "You can move to the Lightning engine at any time"
            }), _jsx(_components.td, {
              children: "You can't move to the Basic engine once Lightning is configured"
            })]
          })]
        })]
      }), "\n", _jsx(_components.h3, {
        id: "when-the-lightning-sync-engine-is-required",
        children: "When the Lightning sync engine is required"
      }), "\n", _jsx(_components.p, {
        children: "The Basic sync engine supports standard audience syncs, but several Hightouch features require the Lightning sync engine. Enable the Lightning sync engine if you plan to use any of the following:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/customer-studio/journeys",
            children: "Journeys"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/customer-studio/sampling",
            children: "Sampling"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "Warehouse Sync Logs"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/customer-studio/audience-snapshots",
            children: "Audience Snapshots"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["Experiment ", _jsx(_components.a, {
            href: "/customer-studio/experiments",
            children: "holdout group logs"
          })]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/match-booster/overview",
            children: "Match Booster"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://hightouch.com/platform/identity-resolution",
            children: "Identity Resolution"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "If you are setting up Customer Studio for anything beyond basic audience syncs, Hightouch recommends enabling the Lightning sync engine during source setup so you don't need to revisit your sync engine choice later."
      }), "\n", _jsx(_components.h2, {
        id: "supported-sources",
        children: "Supported sources"
      }), "\n", _jsx(_components.p, {
        children: "The Lightning sync engine works with the following sources:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-athena",
            children: "Amazon Athena"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/amazon-redshift",
            children: "Amazon Redshift"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/synapse",
            children: "Azure Synapse"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/clickhouse",
            children: "ClickHouse"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/databricks",
            children: "Databricks"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/google-bigquery",
            children: "Google BigQuery"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/greenplum",
            children: "Greenplum Database"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/lakebase",
            children: "Lakebase"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/fabric",
            children: "Microsoft Fabric"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/postgresql",
            children: "PostgreSQL"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/snowflake",
            children: "Snowflake"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/snowflake-iceberg",
            children: "Snowflake Iceberg"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "/sources/trino",
            children: "Trino"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "enable-the-lightning-sync-engine",
        children: "Enable the Lightning sync engine"
      }), "\n", _jsxs(_components.p, {
        children: ["When first setting up one of the ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "supported sources"
        }), ", Hightouch prompts you to ", _jsx(_components.strong, {
          children: "Choose your sync engine"
        }), ". Select ", _jsx(_components.strong, {
          children: "Lightning sync engine"
        }), ", marked ", _jsx(_components.strong, {
          children: "Recommended"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/lightning-sync-engine/choose-sync-engine.png",
          alt: "The Choose your sync engine step with Lightning and Basic options"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["You can set up the Lightning sync engine at this point or continue with the Basic sync engine and ", _jsx(_components.a, {
          href: "#migrate-from-basic-sync-engine",
          children: "upgrade to the Lightning engine"
        }), " later. Once you've configured the Lightning engine, you can't switch back to the Basic engine."]
      }), "\n", _jsxs(_components.p, {
        children: ["Selecting the Lightning sync engine surfaces a SQL snippet that provisions a dedicated user and role and creates the ", _jsx(_components.a, {
          href: "#warehouse-schemas",
          children: "warehouse schemas"
        }), " Hightouch needs. You must:"]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Grant Hightouch write permission to your data warehouse."
        }), "\n", _jsx(_components.li, {
          children: "Create the warehouse schemas for Hightouch to use."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "Run the snippet from the source's in-app instructions, editing the placeholders for your warehouse first."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/lightning-sync-engine/sync-engine-permissions.png",
          alt: "The permissions snippet that provisions Hightouch's warehouse user, role, and schemas"
        })
      }), "\n", _jsx(_components.h3, {
        id: "warehouse-schemas",
        children: "Warehouse schemas"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch requires you to create two schemas:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_planner"
          }), ": the internal schema that powers the Lightning sync engine. Hightouch manages it, and you don't query it directly."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_audit"
          }), ": the public schema that holds ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "Warehouse Sync Logs"
          }), " and other tables you can query."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To rename these or store them in a different database, expand ", _jsx(_components.strong, {
          children: "Override schema names and locations"
        }), " during setup."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Storing the Lightning schema in a different database or project can limit some\nsources to SQL models. See your ", _jsx(_components.a, {
            href: "#supported-sources",
            children: "source's documentation"
          }), "\nfor details."]
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/lightning-sync-engine/override-schema-names.png",
          alt: "Overriding the internal and public schema names during setup"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["Whenever a sync that uses the Lightning sync engine runs, Hightouch creates two new tables in the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " schema: one to log the model's query results (the ", _jsx(_components.code, {
          children: "_plan"
        }), " table) and one to log rows rejected during the sync (the ", _jsx(_components.code, {
          children: "_rejections"
        }), " table).\nHightouch uses these tables to perform ", _jsx(_components.a, {
          href: "#how-the-lightning-sync-engine-works",
          children: "change data capture"
        }), " and plan which rows to sync."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch only keeps the two most recent pairs of tables to compute the diff for the current sync.\nIn other words, Hightouch ", _jsx(_components.a, {
          href: "/syncs/cdc#faq",
          children: "doesn't maintain historical records"
        }), " of these tables, and any given sync never has more than the two most recent pairs of tables.\nBecause these tables and their names change with every sync run, Hightouch requires permission to write to the entire ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " schema rather than specific tables."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can learn more about the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema on the ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs#schema",
            children: "Warehouse Sync\nLogs"
          }), " page."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "table-removal",
        children: "Table removal"
      }), "\n", _jsxs(_components.p, {
        children: ["Removing tables from the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " and ", _jsx(_components.code, {
          children: "hightouch_audit"
        }), " schemas isn't recommended:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_planner"
          }), ": these tables are required for ", _jsx(_components.a, {
            href: "#how-the-lightning-sync-engine-works",
            children: "change data capture"
          }), " to work correctly; if you delete these tables, you need to trigger a ", _jsx(_components.a, {
            href: "/syncs/resync-reset-clear#full-resync",
            children: "full resync"
          }), " to fix your syncs."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "hightouch_audit"
          }), ": removing tables from the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema would mean deleting the ", _jsx(_components.a, {
            href: "/syncs/warehouse-sync-logs",
            children: "Warehouse Sync Logs"
          }), " related to a sync's prior runs, which are useful for historical analysis."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If your ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "source"
        }), " is located in the European Union, you might want to avoid having PII older than 30 days in your data warehouse, in accordance with the ", _jsx(_components.a, {
          href: "https://en.wikipedia.org/wiki/General_Data_Protection_Regulation",
          children: "GDPR"
        }), ".\nIn this case, we recommend running ", _jsx(_components.strong, {
          children: "all"
        }), " syncs in your workspace at least once every 15 days.\nThis ensures that Hightouch refreshes the tables in the ", _jsx(_components.code, {
          children: "hightouch_planner"
        }), " schema and the ", _jsx(_components.strong, {
          children: "Snapshot"
        }), " table in the ", _jsxs(_components.a, {
          href: "/syncs/warehouse-sync-logs#schema",
          children: [_jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema"]
        }), ", removing all older PII before it reaches the 30-day threshold."]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Historical data is never automatically cleared out from the ", _jsx(_components.strong, {
            children: "Changelog"
          }), "\ntable in the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema. If required, you need to manually\nremove older PII from this table."]
        })
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "Sync runs"
          }), " table in the ", _jsx(_components.code, {
            children: "hightouch_audit"
          }), " schema doesn't contain PII."]
        })
      }), "\n", _jsx(_components.h2, {
        id: "migrate-from-basic-sync-engine",
        children: "Migrate from Basic sync engine"
      }), "\n", _jsxs(_components.p, {
        children: ["You can migrate to the Lightning sync engine by going to the ", _jsx(_components.strong, {
          children: "Configuration"
        }), " tab of any previously setup ", _jsx(_components.a, {
          href: "#supported-sources",
          children: "supported source"
        }), "."]
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["From the source's ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab, select ", _jsx(_components.strong, {
            children: "Lightning sync engine"
          }), " under ", _jsx(_components.strong, {
            children: "Choose your sync engine"
          }), " ."]
        }), "\n", _jsx(_components.li, {
          children: "Follow the source-specific in-app instructions to grant Hightouch write permissions to your warehouse and create the schemas for Hightouch to use."
        }), "\n", _jsxs(_components.li, {
          children: ["Click ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Test your connection to ensure setup is correct."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "workspace-management/permissions/lightning-engine-migration.png",
          alt: "Migrating an existing source to the Lightning sync engine from its Configuration tab"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you enable the Lightning sync engine on an existing source, Hightouch migrates any syncs on the Basic engine to the Lightning sync engine on the next run. Any previously synced data won't be unnecessarily resynced since the existing ", _jsx(_components.a, {
          href: "#how-the-lightning-sync-engine-works",
          children: "change data capture"
        }), " data is copied over to your warehouse schemas. In most cases you don't need to trigger ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#full-resync",
          children: "full resyncs"
        }), " or ", _jsx(_components.a, {
          href: "/syncs/resync-reset-clear#reset-cdc",
          children: "reset change data capture (CDC)"
        }), ", unless you ", _jsx(_components.a, {
          href: "#migration-tips",
          children: "encounter errors"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["The first run after migrating to the Lightning sync engine can take longer\nthan usual, as it uses a special migration sync engine to create the necessary\n", _jsx(_components.a, {
            href: "#warehouse-schemas",
            children: "schemas"
          }), " in your warehouse."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "migration-tips",
        children: "Migration tips"
      }), "\n", _jsx(_components.p, {
        children: "Follow these tips when performing the migration:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["The Lightning sync engine requires your ", _jsx(_components.a, {
            href: "#primary-key-is-not-unique",
            children: "primary key to be unique"
          }), " for every model using the source. If you're unsure if your primary key is unique, check before migrating."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you have a large number of syncs to migrate, please\n", _jsx(PylonLink, {
            pylonText: "Hello, I'm migrating to the Lightning sync engine and have a large number of syncs. Could you please help us migrate incrementally?",
            linkText: "reach out"
          }), " so that we can help you migrate incrementally."]
        }), "\n", _jsxs(_components.li, {
          children: ["If you hit any errors, you can often fix them with a ", _jsx(_components.a, {
            href: "/syncs/resync-reset-clear#full-resync",
            children: "full resync"
          }), ". Ensure your sync meets the ", _jsx(_components.a, {
            href: "/syncs/resync-reset-clear#prerequisites",
            children: "full resync prerequisites"
          }), " before doing so. Otherwise, you can ", _jsx(_components.a, {
            href: "/syncs/resync-reset-clear#reset-cdc",
            children: "reset change data capture (CDC)"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(_components.h3, {
        id: "primary-key-is-not-unique",
        children: "Primary key is not unique"
      }), "\n", _jsxs(_components.p, {
        children: ["The Lightning sync engine requires that every row in your model has a unique value for its ", _jsx(_components.a, {
          href: "/models/creating-models#unique-primary-key-requirement",
          children: "primary key"
        }), ". This is because the primary key is used to uniquely identify each row, and for example, whether it failed previously."]
      }), "\n", _jsxs(_components.p, {
        children: ["Models with duplicate primary keys can be deduplicated in SQL with the ", _jsx(_components.code, {
          children: "ROW_NUMBER()"
        }), " function."]
      }), "\n", _jsx(_components.p, {
        children: "For example:"
      }), "\n", _jsx(_components.pre, {
        children: _jsxs(_components.code, {
          className: "hljs language-sql",
          children: [_jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " your_data_model ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "copy"
          }), _jsx(_components.span, {
            className: "hljs-operator",
            children: "/"
          }), "paste your model here\n)\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WITH"
          }), " your_data_model_with_rank ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "AS"
          }), " (\n  ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), ", ", _jsx(_components.span, {
            className: "hljs-built_in",
            children: "ROW_NUMBER"
          }), "() ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "OVER"
          }), " (", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "PARTITION"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "BY"
          }), " your_primary_key) ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "as"
          }), " rank ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " your_data_model\n)\n\n", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "SELECT"
          }), " ", _jsx(_components.span, {
            className: "hljs-operator",
            children: "*"
          }), " ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "FROM"
          }), " your_data_model_with_rank ", _jsx(_components.span, {
            className: "hljs-keyword",
            children: "WHERE"
          }), " rank", _jsx(_components.span, {
            className: "hljs-operator",
            children: "="
          }), _jsx(_components.span, {
            className: "hljs-number",
            children: "1"
          }), "\n"]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["The preceding example de-duplicates your model by ", _jsx(_components.em, {
          children: "partitioning"
        }), " on the primary key. It chooses an arbitrary record from your duplicates. You can use ", _jsx(_components.code, {
          children: "ORDER BY"
        }), " or ", _jsx(_components.code, {
          children: "WHERE"
        }), " to filter and select which data from the duplicates you want to keep."]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
